import { AppQueryParams } from '@doda/common/src/constants/queryParams';
import { RANGE_LIST } from '@doda/common/src/constants/searchConditionModal';
import {
  HA_MASTER_ITEMS_LOWER,
  HA_MASTER_ITEMS_UPPER,
  HA_SEARCH_ITEMS,
} from '@doda/common/src/constants/searchItems';
import { sc, tlReadyExecute } from '@doda/common/src/utils/adobeAnalyticsUtil';
import { cassifyListAsLMS } from '@doda/common/src/utils/convArrayUtil';
import { dataLayer } from '@doda/common/src/utils/googleTagManagerUtil';
import { AppDispatch } from 'redux/store';
import { JOB_COUNT_PER_PAGE } from 'constants/jobList';
import { calcJobCountPerPage } from '@doda/common/src/logics/calcJobCountPerPage';

/**
 * 資格経験スキル一覧画面PV計測AA
 *
 * @param {Object} param - パラメータのオブジェクト
 * @param {string} param.pageName - ページ名
 * @param {string} param.channel - Channel名
 * @param {number} param.numOfVideo - 動画求人数
 * @param {number} param.numOfJob - 求人数
 * @param {AppQueryParams} param.query - クエリパラメータ
 * @returns {void}
 */
export const measureImpressionCareerRecommendJobListAA = ({
  pageName,
  channel,
  numOfVideo,
  numOfJob,
  query,
}: {
  pageName: string;
  channel: string;
  numOfVideo: number;
  numOfJob: number;
  query: AppQueryParams;
}) => {
  const {
    lList: ocL,
    mList: ocM,
    sList: ocS,
  } = cassifyListAsLMS(query.oc ?? []);
  const { lList: indL, sList: indS } = cassifyListAsLMS(query.ind ?? []);
  const jobCountPerPage = calcJobCountPerPage(query.so, JOB_COUNT_PER_PAGE);

  const events: string[] = [];
  // 動画求人がある場合
  if (numOfVideo) events.push('event97');
  // 0件の場合
  if (numOfJob === 0) {
    events.push('event83');
  }
  sc({
    pageName,
    channel,
    linkTrackVars: numOfVideo
      ? 'pageName,channel,prop6,prop7,prop9,prop10,prop11,prop12,prop13,prop14,prop15,prop16,prop17,prop20,prop48,prop50,prop51,prop52,prop53,prop55,eVar56,eVar119,events'
      : 'pageName,channel,prop6,prop7,prop9,prop10,prop11,prop12,prop13,prop14,prop15,prop16,prop17,prop20,prop48,prop50,prop51,prop52,prop53,prop55,events',
    prop6: query.ci ? query.ci.join(',') : '',
    prop7: query.st ? query.st.join(',') : '',
    prop9: ocL ? ocL.join(',') : undefined,
    prop10: ocM ? ocM.join(',') : undefined,
    prop11: ocS ? ocS.join(',') : undefined,
    prop12: query.ar ? query.ar.join(',') : '',
    prop13: query.pr ? query.pr.join(',') : '',
    prop14: query.k ? `\u691C\u7D22:${query.k}` : '',
    prop15: query.op ? query.op.join(',') : '',
    prop16: indL ? indL.join(',') : undefined,
    prop17: indS ? indS.join(',') : undefined,
    prop20: query.op
      ? query.op
          .filter((param) => ['17', '18', '19'].some((el) => el === param))
          .join(',')
      : '',
    prop48: query.rt ? query.rt.join(',') : '',
    prop50: query.ha
      ? HA_SEARCH_ITEMS.find(
          (item) => item.id === (query.ha !== undefined ? query.ha[0] : '0')
        )?.name
      : '',
    prop51: query.ha
      ? HA_SEARCH_ITEMS.find(
          (item) => item.id === (query.ha !== undefined ? query.ha[1] : '0')
        )?.name
      : '',
    prop52: query.kwc
      ? RANGE_LIST.find((range) => query.kwc === range.value)?.label
      : '',
    prop53: (() => {
      if (query.tp === '2') return '\u76F4\u63A5\u5FDC\u52DF';
      if (query.tp === '0') return '\u8EE2\u8077\u652F\u63F4';
      return '\u5168\u6C42\u4EBA';
    })(),
    prop55: `\u30DA\u30FC\u30B8\u5185\u8868\u793A\u4EF6\u6570:${jobCountPerPage}`,
    eVar56: numOfVideo ? String(numOfVideo) : undefined,
    eVar119: numOfVideo ? 'prime' : undefined,
    linkTrackEvents: events.join(','),
    events: events.join(','),
  });
};

const INCLUDED_KEYWORD_CATEGORY = {
  '1': 'すべて',
  '2': '社名',
  '3': '職種名',
  '4': '勤務地',
  '9': 'スキル',
};

/**
 * 資格経験スキル一覧画面PV計測GA
 *
 * @param {Object} param - パラメータのオブジェクト
 * @param {string} param.pageName - ページ名
 * @param {string} param.channel - Channel名
 * @param {AppQueryParams} param.query - クエリパラメータ
 */
export const measureImpressionCareerRecommendJobListGA = ({
  pageName,
  channel,
  query,
}: {
  pageName: string;
  channel: string;
  query: AppQueryParams;
}) => {
  const {
    lList: ocL,
    mList: ocM,
    sList: ocS,
  } = cassifyListAsLMS(query.oc ?? []);
  const { lList: indL, sList: indS } = cassifyListAsLMS(query.ind ?? []);
  const salaryLowerId = query.ha
    ? HA_MASTER_ITEMS_LOWER.find(
        (item) => item.id === (query.ha !== undefined ? query.ha[0] : '0')
      )?.masterId
    : '';
  const salaryUpperId = query.ha
    ? HA_MASTER_ITEMS_UPPER.find(
        (item) => item.id === (query.ha !== undefined ? query.ha[1] : '0')
      )?.masterId
    : '';
  const optionsIdList: string[] = [];
  if (query.op) {
    query.op.forEach((item) => {
      optionsIdList.push(item);
    });
  }
  if (typeof salaryLowerId === 'string' && salaryLowerId !== '') {
    optionsIdList.push(salaryLowerId);
  }
  if (typeof salaryUpperId === 'string' && salaryUpperId !== '') {
    optionsIdList.push(salaryUpperId);
  }
  dataLayer({
    PageName: pageName,
    Channel: channel,
    BizLargeClass: ocL ? ocL.join(',') : '',
    BizMiddleClass: ocM ? ocM.join(',') : '',
    BizSmallClass: ocS ? ocS.join(',') : '',
    WorkArea: query.ar ? query.ar.join(',') : '',
    WorkPrefecture: query.pr ? query.pr.join(',') : '',
    FreeWordSearch: query.k ? `検索:${query.k}` : '',
    SearchOption: optionsIdList ? optionsIdList.join(',') : '',
    IndLargeClass: indL ? indL.join(',') : '',
    IndSmallClass: indS ? indS.join(',') : '',
    EmpPattern: query.op
      ? query.op
          .filter((param) => ['17', '18', '19'].some((el) => el === param))
          .join(',')
      : '',
    WishLowSalary: salaryLowerId,
    WishUppSalary: salaryUpperId,
    KeywordSearchSeg: query.kwc
      ? INCLUDED_KEYWORD_CATEGORY[
          query.kwc[0] as keyof typeof INCLUDED_KEYWORD_CATEGORY
        ]
      : '',
    JobListTab: (() => {
      if (query.tp === '2') return '\u76F4\u63A5\u5FDC\u52DF';
      if (query.tp === '0') return '\u8EE2\u8077\u652F\u63F4';
      return '\u5168\u6C42\u4EBA';
    })(),
  });
};

/**
 * 資格経験スキル一覧画面「気になる」クリック計測
 * @param {boolean} isAdobeScript - AAスクリプト読み込みステータス
 * @param {AppDispatch} dispatch - dispatch関数
 * @param {Object} param - パラメータのオブジェクト
 * @param {string} [param.jid] - 求人ID
 */
export const measureOnClickBookmark = (
  isAdobeScript: boolean,
  dispatch: AppDispatch,
  { jid = '' }: { jid?: string }
) => {
  // AA計測
  tlReadyExecute(
    isAdobeScript,
    dispatch,
    'kininaruClick_CareerRecommendJobList',
    {
      linkTrackVars:
        'channel,events,products,prop1,prop23,prop32,prop69,eVar23,eVar78',
      linkTrackEvents: 'event120',
      events: 'event120',
      products: `;${jid}`,
      prop23: 'kininaru_Click',
      prop32: jid,
      eVar23: 'D=c23',
    }
  );
  // GA計測
  dataLayer({
    JobId: jid,
    event: 'bookmark_click',
  });
};

/**
 * 資格経験スキル一覧画面 直接応募求人「応募する」クリック計測
 *
 * @param {boolean} isAdobeScript - AAスクリプト読み込みステータス
 * @param {AppDispatch} dispatch - dispatch関数
 * @param {Object} param - パラメータのオブジェクト
 * @param {string} [param.jid] - 求人ID
 */
export const measureOnClickApply = (
  isAdobeScript: boolean,
  dispatch: AppDispatch,
  { jid = '' }: { jid?: string }
) => {
  // AA計測
  tlReadyExecute(isAdobeScript, dispatch, 'Chokusetsu Ichiran', {
    linkTrackVars: 'channel,events,prop23,eVar23',
    linkTrackEvents: 'event8',
    events: 'event8',
    products: `;${jid}`,
    prop23: '求人一覧ボタン',
    prop32: jid,
    eVar23: 'D=c23',
  });
  // GA計測
  dataLayer({
    click_Label: 'event8',
    click_Action: '求人一覧ボタン',
    click_Category: 'Chokusetsu Ichiran',
    event: 'setEffect',
  });
};

/**
 * 資格経験スキル一覧画面 紹介求人の「応募する」クリック計測
 *
 * @param {boolean} isAdobeScript - AAスクリプト読み込みステータス
 * @param {AppDispatch} dispatch - dispatch関数
 * @param {Object} param - パラメータのオブジェクト
 * @param {string} [param.jid] - 求人ID
 */
export const measureOnClickAgentApply = (
  isAdobeScript: boolean,
  dispatch: AppDispatch,
  { jid = '' }: { jid?: string }
) => {
  // AA計測
  tlReadyExecute(isAdobeScript, dispatch, 'Shokai Ichiran', {
    linkTrackVars: 'channel,events,prop23,eVar23',
    linkTrackEvents: 'event5',
    events: 'event5',
    products: `;${jid}`,
    prop23: '求人一覧ボタン',
    prop32: jid,
    eVar23: 'D=c23',
  });
  // GA計測
  dataLayer({
    click_Label: 'event5',
    click_Action: '求人一覧ボタン',
    click_Category: 'Shokai Ichiran',
    event: 'setEffect',
  });
};

/**
 * 資格経験スキル一覧画面「0件」表示計測
 *
 * @param {boolean} isAdobeScript - AAスクリプト読み込みステータス
 * @param {AppDispatch} dispatch - dispatch関数
 */
export const measureVisibleNoResult = (
  isAdobeScript: boolean,
  dispatch: AppDispatch
) => {
  // AA計測
  tlReadyExecute(isAdobeScript, dispatch, 'PC_Jobserch_hit0', {
    linkTrackVars: 'channel,events,prop23,eVar23',
    prop23: 'PC_Jobserch_hit0',
    eVar23: 'D=c23',
  });
  // GA計測
  dataLayer({
    click_Label: 'prop23,eVal23',
    click_Action: 'PC_Jobserch_hit0',
    click_Category: 'PC_Jobserch_hit0',
    event: 'setEffect',
  });
};
